import { DEFAULT_CARD_DECK, type CardDeckId } from "@bing/shared";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { CARD_DECKS } from "./card-decks";
import { cardFacesStatus, loadCardFaces, subscribeCardFaces } from "./card-faces";
import {
  deckReady,
  deckStatus,
  loadDeck,
  loadDeckSample,
  loadedFaceSrc,
  subscribeDecks,
} from "./deck-assets";

/**
 * React adapters over the framework-free loaders: `card-faces.ts` (the classic SVG chunk, which
 * «استاندارد» draws and every other deck falls back to) and `deck-assets.ts` (the image decks).
 */

/** Does this deck draw the bundled SVG chunk itself? (Only `standard`.) */
export function deckNeedsFaces(deck: CardDeckId): boolean {
  return CARD_DECKS[deck]?.faces == null;
}

export interface CardFacesGate {
  /** True when the table may render. */
  ready: boolean;
  /** Every retry is spent and nothing drawable is here: show the recovery screen. */
  failed: boolean;
  retry: () => void;
}

/**
 * Gate on the classic SVG chunk alone (`needed` false → always ready).
 *
 * ⚠️ IT ALSO KICKS A FRESH ROUND WHEN A PREVIOUS ONE FAILED. The after-boot prefetch can fail
 * minutes before the player taps حکم, on a network that has since recovered — opening straight
 * onto the recovery screen without even trying would be wrong, and on these connections a
 * transient failure is the common case, not the exception. Guarded by a per-mount ref so a round
 * that fails again settles on the recovery screen instead of looping.
 */
export function useCardFaces(needed: boolean): CardFacesGate {
  const status = useSyncExternalStore(subscribeCardFaces, cardFacesStatus, () => "ready" as const);
  const kicked = useRef(false);

  useEffect(() => {
    if (!needed || kicked.current) return;
    if (status === "idle" || status === "failed") {
      kicked.current = true;
      void loadCardFaces().catch(() => undefined);
    }
  }, [needed, status]);

  const retry = useCallback(() => {
    void loadCardFaces().catch(() => undefined);
  }, []);

  return { ready: !needed || status === "ready", failed: needed && status === "failed", retry };
}

/**
 * THE DECK A CARD IS DRAWN WITH: the asked deck once it is fully loaded (`deck-assets.ts`), and
 * «استاندارد» until then — never a face that is not in memory. Re-renders when that changes.
 */
export function useRenderDeck(deck: CardDeckId): CardDeckId {
  const ready = useSyncExternalStore(
    subscribeDecks,
    () => deckReady(deck),
    () => deckReady(deck),
  );
  return ready ? deck : DEFAULT_CARD_DECK;
}

/**
 * GATE A CARD TABLE (2026-10-20). The table needs SOMETHING it can draw: the picked deck, or
 * «استاندارد» while the picked one is still on its way (`deck-assets.ts`'s rule — a table never waits
 * for an image deck). So:
 *   - it starts the picked deck downloading in the background — again whenever the deck CHANGES
 *     (an online table's `settings.deck` lands a frame after the join; a reconnect starts on the
 *     default and learns the host's deck one frame later);
 *   - it starts the classic chunk too while the picked deck is not ready (the fallback must be
 *     drawable);
 *   - `ready`: the picked deck is ready, or the classic chunk is;
 *   - `failed`: neither can be had — the classic chunk spent its retries AND the picked deck is
 *     `standard` itself or failed too. A picked image deck still downloading is not a failure: the
 *     table waits for whichever arrives first.
 * `isCardGame` false (board games) → always ready, nothing loaded.
 */
export function useDeckGate(deck: CardDeckId, isCardGame = true): CardFacesGate {
  const snap = (): string => `${deckStatus(deck)}|${cardFacesStatus()}`;
  const state = useSyncExternalStore(subscribeDecks, snap, snap);
  const [deckSt, facesSt] = state.split("|");
  const kickedDeck = useRef<CardDeckId | null>(null);
  const kickedFaces = useRef(false);

  useEffect(() => {
    if (!isCardGame) return;
    // the picked deck: once per deck value (a CHANGE re-arms it), and again if it had failed
    if (kickedDeck.current !== deck && deckSt !== "ready" && deckSt !== "loading") {
      kickedDeck.current = deck;
      void loadDeck(deck).catch(() => undefined);
    }
    // the fallback: needed only while the picked deck is not ready
    if (
      deckSt !== "ready" &&
      !kickedFaces.current &&
      (facesSt === "idle" || facesSt === "failed")
    ) {
      kickedFaces.current = true;
      void loadCardFaces().catch(() => undefined);
    }
  }, [isCardGame, deck, deckSt, facesSt]);

  const retry = useCallback(() => {
    void loadCardFaces().catch(() => undefined);
    void loadDeck(deck).catch(() => undefined);
  }, [deck]);

  if (!isCardGame) return { ready: true, failed: false, retry };
  const ready = deckSt === "ready" || facesSt === "ready";
  const deckGone = deckNeedsFaces(deck) || deckSt === "failed";
  return { ready, failed: !ready && facesSt === "failed" && deckGone, retry };
}

/**
 * The deck picker's SAMPLE of one deck: fetches just that one face (`KD`), so every deck's look can
 * be shown without downloading four decks. `shown` once that face is held (`standard`: once its
 * chunk is in). A sample that cannot be had is asked again, a few rounds apart (each round is the
 * loader's own five attempts) — a tile must not stay a skeleton for the life of the screen.
 */
export const SAMPLE_ROUNDS = 3;
export const SAMPLE_ROUND_GAP_MS = 4000;

export function useDeckSample(deck: CardDeckId, cardId: string): { shown: boolean } {
  const snap = (): boolean =>
    deckNeedsFaces(deck) ? cardFacesStatus() === "ready" : loadedFaceSrc(deck, cardId) !== null;
  const shown = useSyncExternalStore(subscribeDecks, snap, snap);
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (shown || round >= SAMPLE_ROUNDS) return;
    let live = true;
    let timer: number | undefined;
    void loadDeckSample(deck, cardId).then((ok) => {
      if (!ok && live) timer = window.setTimeout(() => setRound((n) => n + 1), SAMPLE_ROUND_GAP_MS);
    });
    return () => {
      live = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [deck, cardId, shown, round]);
  return { shown };
}
